
import { useState, useEffect, FormEvent } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function ContactPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState<'store' | 'producer'>('store');
  const [storeSubmitting, setStoreSubmitting] = useState(false);
  const [producerSubmitting, setProducerSubmitting] = useState(false);
  const [storeSuccess, setStoreSuccess] = useState(false);
  const [producerSuccess, setProducerSuccess] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleStoreSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStoreSubmitting(true);
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    try {
      await fetch('https://readdy.ai/api/form/d67b23dbd17lru29kcsg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      setStoreSuccess(true);
      form.reset();
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setStoreSubmitting(false);
    }
  };

  const handleProducerSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setProducerSubmitting(true);
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    try {
      await fetch('https://readdy.ai/api/form/d67b23dbd17lru29kct0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      setProducerSuccess(true);
      form.reset();
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setProducerSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar isScrolled={isScrolled} />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Let's Work Together
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Whether you're a store looking for quality snack products or a producer seeking distribution, we're here to help. Fill out the form below and we'll be in touch within 48 hours.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-3 gap-16">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Get in Touch</h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-md">
                    <i className="ri-map-pin-line text-gray-700 text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Location</h3>
                    <p className="text-gray-600 text-sm">Sonoma County, California</p>
                    <p className="text-gray-500 text-sm">Serving all of Sonoma County</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-md">
                    <i className="ri-mail-line text-gray-700 text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                    <a href="mailto:hello@sonomaroutes.com" className="text-gray-600 text-sm hover:text-gray-900 cursor-pointer">
                      hello@sonomaroutes.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-md">
                    <i className="ri-phone-line text-gray-700 text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Phone</h3>
                    <a href="tel:+17075551234" className="text-gray-600 text-sm hover:text-gray-900 cursor-pointer">
                      (707) 555-1234
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-md">
                    <i className="ri-time-line text-gray-700 text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Business Hours</h3>
                    <p className="text-gray-600 text-sm">Monday - Friday: 7am - 5pm</p>
                    <p className="text-gray-500 text-sm">Routes run daily</p>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="mt-10">
                <div className="w-full h-48 rounded-lg overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d399566.8283895791!2d-123.10831565!3d38.52460285!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8085b4e2c8e9e8e7%3A0x1c3c3c3c3c3c3c3c!2sSonoma%20County%2C%20CA!5e0!3m2!1sen!2sus!4v1620000000000!5m2!1sen!2sus"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Sonoma County Map"
                  ></iframe>
                </div>
              </div>
            </div>

            {/* Forms */}
            <div className="lg:col-span-2">
              {/* Tab Switcher */}
              <div className="bg-gray-100 p-1 rounded-full inline-flex mb-8">
                <button
                  onClick={() => setActiveTab('store')}
                  className={`px-6 py-2.5 text-sm font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                    activeTab === 'store'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Store Inquiry
                </button>
                <button
                  onClick={() => setActiveTab('producer')}
                  className={`px-6 py-2.5 text-sm font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                    activeTab === 'producer'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Producer Inquiry
                </button>
              </div>

              {/* Store Inquiry Form */}
              {activeTab === 'store' && (
                <div>
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Store Inquiry</h2>
                    <p className="text-gray-600">
                      Interested in stocking quality snack products? Tell us about your store and we'll reach out to discuss placement options.
                    </p>
                  </div>

                  {storeSuccess ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                      <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                        <i className="ri-check-line text-green-600 text-2xl"></i>
                      </div>
                      <h3 className="text-lg font-semibold text-green-900 mb-2">Thank You!</h3>
                      <p className="text-green-700">We've received your inquiry and will be in touch within 48 hours.</p>
                      <button
                        onClick={() => setStoreSuccess(false)}
                        className="mt-4 text-sm text-green-600 hover:text-green-800 cursor-pointer"
                      >
                        Submit another inquiry
                      </button>
                    </div>
                  ) : (
                    <form
                      id="store-inquiry-form"
                      data-readdy-form
                      onSubmit={handleStoreSubmit}
                      className="space-y-6"
                    >
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="store-name" className="block text-sm font-medium text-gray-700 mb-2">
                            Store Name *
                          </label>
                          <input
                            type="text"
                            id="store-name"
                            name="store_name"
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all"
                            placeholder="Your store name"
                          />
                        </div>
                        <div>
                          <label htmlFor="store-contact" className="block text-sm font-medium text-gray-700 mb-2">
                            Contact Name *
                          </label>
                          <input
                            type="text"
                            id="store-contact"
                            name="contact_name"
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all"
                            placeholder="Your name"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="store-email" className="block text-sm font-medium text-gray-700 mb-2">
                            Email *
                          </label>
                          <input
                            type="email"
                            id="store-email"
                            name="email"
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all"
                            placeholder="you@example.com"
                          />
                        </div>
                        <div>
                          <label htmlFor="store-phone" className="block text-sm font-medium text-gray-700 mb-2">
                            Phone
                          </label>
                          <input
                            type="tel"
                            id="store-phone"
                            name="phone"
                            className="w-full px-4 py-3 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all"
                            placeholder="(707) 555-1234"
                          />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="store-address" className="block text-sm font-medium text-gray-700 mb-2">
                          Store Address *
                        </label>
                        <input
                          type="text"
                          id="store-address"
                          name="store_address"
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all"
                          placeholder="123 Main St, Santa Rosa, CA"
                        />
                      </div>

                      <div>
                        <label htmlFor="store-type" className="block text-sm font-medium text-gray-700 mb-2">
                          Store Type *
                        </label>
                        <select
                          id="store-type"
                          name="store_type"
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all cursor-pointer"
                        >
                          <option value="">Select store type</option>
                          <option value="convenience">Convenience Store</option>
                          <option value="grocery">Independent Grocery</option>
                          <option value="liquor">Liquor Store</option>
                          <option value="deli">Deli / Market</option>
                          <option value="gas">Gas Station</option>
                          <option value="other">Other</option>
                        </select>
                      </div>

                      <div>
                        <label htmlFor="store-categories" className="block text-sm font-medium text-gray-700 mb-2">
                          Product Categories of Interest
                        </label>
                        <div className="grid grid-cols-3 gap-3">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" name="categories" value="jerky" className="w-4 h-4 text-gray-900 rounded cursor-pointer" />
                            <span className="text-sm text-gray-700">Jerky</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" name="categories" value="snacks" className="w-4 h-4 text-gray-900 rounded cursor-pointer" />
                            <span className="text-sm text-gray-700">Packaged Snacks</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" name="categories" value="local" className="w-4 h-4 text-gray-900 rounded cursor-pointer" />
                            <span className="text-sm text-gray-700">Local Specialty</span>
                          </label>
                        </div>
                      </div>

                      <div>
                        <label htmlFor="store-message" className="block text-sm font-medium text-gray-700 mb-2">
                          Additional Information
                        </label>
                        <textarea
                          id="store-message"
                          name="message"
                          rows={4}
                          maxLength={500}
                          className="w-full px-4 py-3 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all resize-none"
                          placeholder="Tell us about your store, current snack offerings, or any specific products you're looking for..."
                        ></textarea>
                      </div>

                      <button
                        type="submit"
                        disabled={storeSubmitting}
                        className="w-full px-8 py-4 bg-gray-900 text-white text-base font-semibold rounded-md hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {storeSubmitting ? 'Submitting...' : 'Submit Store Inquiry'}
                      </button>
                    </form>
                  )}
                </div>
              )}

              {/* Producer Inquiry Form */}
              {activeTab === 'producer' && (
                <div>
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Producer Inquiry</h2>
                    <p className="text-gray-600">
                      Have a product you'd like to get into local stores? Tell us about your brand and we'll discuss distribution opportunities.
                    </p>
                  </div>

                  {producerSuccess ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                      <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                        <i className="ri-check-line text-green-600 text-2xl"></i>
                      </div>
                      <h3 className="text-lg font-semibold text-green-900 mb-2">Thank You!</h3>
                      <p className="text-green-700">We've received your inquiry and will be in touch within 48 hours.</p>
                      <button
                        onClick={() => setProducerSuccess(false)}
                        className="mt-4 text-sm text-green-600 hover:text-green-800 cursor-pointer"
                      >
                        Submit another inquiry
                      </button>
                    </div>
                  ) : (
                    <form
                      id="producer-inquiry-form"
                      data-readdy-form
                      onSubmit={handleProducerSubmit}
                      className="space-y-6"
                    >
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="producer-company" className="block text-sm font-medium text-gray-700 mb-2">
                            Company / Brand Name *
                          </label>
                          <input
                            type="text"
                            id="producer-company"
                            name="company_name"
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all"
                            placeholder="Your company name"
                          />
                        </div>
                        <div>
                          <label htmlFor="producer-contact" className="block text-sm font-medium text-gray-700 mb-2">
                            Contact Name *
                          </label>
                          <input
                            type="text"
                            id="producer-contact"
                            name="contact_name"
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all"
                            placeholder="Your name"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="producer-email" className="block text-sm font-medium text-gray-700 mb-2">
                            Email *
                          </label>
                          <input
                            type="email"
                            id="producer-email"
                            name="email"
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all"
                            placeholder="you@example.com"
                          />
                        </div>
                        <div>
                          <label htmlFor="producer-phone" className="block text-sm font-medium text-gray-700 mb-2">
                            Phone
                          </label>
                          <input
                            type="tel"
                            id="producer-phone"
                            name="phone"
                            className="w-full px-4 py-3 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all"
                            placeholder="(707) 555-1234"
                          />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="producer-website" className="block text-sm font-medium text-gray-700 mb-2">
                          Website
                        </label>
                        <input
                          type="url"
                          id="producer-website"
                          name="website"
                          className="w-full px-4 py-3 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all"
                          placeholder="https://yourcompany.com"
                        />
                      </div>

                      <div>
                        <label htmlFor="producer-category" className="block text-sm font-medium text-gray-700 mb-2">
                          Product Category *
                        </label>
                        <select
                          id="producer-category"
                          name="product_category"
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all cursor-pointer"
                        >
                          <option value="">Select category</option>
                          <option value="jerky">Jerky / Meat Snacks</option>
                          <option value="chips">Chips / Crisps</option>
                          <option value="nuts">Nuts / Trail Mix</option>
                          <option value="bars">Bars / Energy Snacks</option>
                          <option value="candy">Candy / Confections</option>
                          <option value="specialty">Local Specialty Foods</option>
                          <option value="other">Other</option>
                        </select>
                      </div>

                      <div>
                        <label htmlFor="producer-description" className="block text-sm font-medium text-gray-700 mb-2">
                          Product Description *
                        </label>
                        <textarea
                          id="producer-description"
                          name="product_description"
                          rows={3}
                          maxLength={500}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all resize-none"
                          placeholder="Describe your product(s), including key features, pricing, and shelf life..."
                        ></textarea>
                      </div>

                      <div>
                        <label htmlFor="producer-distribution" className="block text-sm font-medium text-gray-700 mb-2">
                          Current Distribution
                        </label>
                        <select
                          id="producer-distribution"
                          name="current_distribution"
                          className="w-full px-4 py-3 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all cursor-pointer"
                        >
                          <option value="">Select current status</option>
                          <option value="none">No current retail distribution</option>
                          <option value="direct">Direct to a few stores</option>
                          <option value="farmers">Farmers markets only</option>
                          <option value="online">Online sales only</option>
                          <option value="regional">Regional distribution</option>
                        </select>
                      </div>

                      <div>
                        <label htmlFor="producer-message" className="block text-sm font-medium text-gray-700 mb-2">
                          Additional Information
                        </label>
                        <textarea
                          id="producer-message"
                          name="message"
                          rows={3}
                          maxLength={500}
                          className="w-full px-4 py-3 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all resize-none"
                          placeholder="Tell us about your production capacity, certifications, or any other relevant details..."
                        ></textarea>
                      </div>

                      <button
                        type="submit"
                        disabled={producerSubmitting}
                        className="w-full px-8 py-4 bg-gray-900 text-white text-base font-semibold rounded-md hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {producerSubmitting ? 'Submitting...' : 'Submit Producer Inquiry'}
                      </button>
                    </form>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-gray-600">Common questions about working with Sonoma Routes.</p>
          </div>

          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">
                <a href="#faq-1" className="hover:text-gray-700">What areas do you serve?</a>
              </h4>
              <p className="text-gray-600 text-sm">
                We currently serve all of Sonoma County, including Santa Rosa, Petaluma, Healdsburg, Sebastopol, Rohnert Park, Windsor, and surrounding areas.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">
                <a href="#faq-2" className="hover:text-gray-700">What's the minimum order for stores?</a>
              </h4>
              <p className="text-gray-600 text-sm">
                We start with small initial placements—typically just a few units per SKU. There's no large minimum order. We believe in proving what sells before scaling up.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">
                <a href="#faq-3" className="hover:text-gray-700">How often do you restock?</a>
              </h4>
              <p className="text-gray-600 text-sm">
                We offer weekly or bi-weekly restocking routes depending on your store's volume and location. We'll work with you to find the right schedule.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">
                <a href="#faq-4" className="hover:text-gray-700">What products do you distribute?</a>
              </h4>
              <p className="text-gray-600 text-sm">
                We focus on shelf-stable snack products including jerky, packaged snacks, and local specialty goods. All products must have appropriate shelf life for retail distribution.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">
                <a href="#faq-5" className="hover:text-gray-700">How do producers get paid?</a>
              </h4>
              <p className="text-gray-600 text-sm">
                We operate on a consignment model for most products. Producers are paid for products sold, typically on a monthly basis. Terms are discussed during onboarding.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
